import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  useToast,
  VStack,
  HStack,
  Text,
  FormHelperText,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  Box,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { db } from '../../services/firebase';
import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { PAYMENT_METHODS, calculateDaysLate, ManualPayment } from '../../types/paymentTypes';

interface LogPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenants: any[];
  propertyId: string;
  onPaymentLogged?: (payment: ManualPayment) => void;
}

export const LogPaymentModal: React.FC<LogPaymentModalProps> = ({
  isOpen,
  onClose,
  tenants,
  propertyId,
  onPaymentLogged,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    tenantId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    method: 'cash',
    reference: '',
    notes: '',
  });

  const [daysLate, setDaysLate] = useState(0);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);

  useEffect(() => {
    if (formData.tenantId && formData.paymentDate) {
      const tenant = tenants.find(t => t.id === formData.tenantId);
      setSelectedTenant(tenant);

      if (tenant?.leaseEndDate) {
        const paymentDate = new Date(formData.paymentDate);
        const dueDate = new Date(
          tenant.leaseEndDate.seconds ? tenant.leaseEndDate.seconds * 1000 : tenant.leaseEndDate
        );

        // Set due date to 1st of month (typical rent due date)
        dueDate.setDate(1);
        const days = calculateDaysLate(paymentDate, dueDate);
        setDaysLate(days);
      }
    }
  }, [formData.tenantId, formData.paymentDate, tenants]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.tenantId || !formData.amount || !formData.paymentDate) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const paymentDate = new Date(formData.paymentDate);

      const paymentData: Omit<ManualPayment, 'id'> = {
        landlordId: user?.uid || '',
        propertyId,
        tenantId: formData.tenantId,
        amount: parseFloat(formData.amount),
        paymentDate: Timestamp.fromDate(paymentDate),
        method: formData.method as any,
        reference: formData.reference,
        notes: formData.notes,
        status: 'pending',
        daysLate,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user?.uid || '',
      };

      const docRef = await addDoc(
        collection(db, 'manualPayments'),
        paymentData
      );

      // Send notification to owner
      await addDoc(
        collection(db, 'paymentApprovalNotifications'),
        {
          landlordId: user?.uid || '',
          tenantId: formData.tenantId,
          paymentId: docRef.id,
          amount: parseFloat(formData.amount),
          paymentDate: Timestamp.fromDate(paymentDate),
          daysLate,
          method: formData.method,
          status: 'pending',
          createdAt: Timestamp.now(),
          expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
        }
      );

      toast({
        title: 'Payment Logged',
        description: `Payment of $${parseFloat(formData.amount).toFixed(2)} logged successfully. Awaiting owner approval.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      if (onPaymentLogged) {
        onPaymentLogged({ ...paymentData, id: docRef.id });
      }

      // Reset form
      setFormData({
        tenantId: '',
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        method: 'cash',
        reference: '',
        notes: '',
      });

      onClose();
    } catch (error) {
      console.error('Error logging payment:', error);
      toast({
        title: 'Error',
        description: 'Failed to log payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Log Manual Payment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              Payments will be sent to owner for approval before posting to tenant account.
            </Alert>

            <FormControl isRequired>
              <FormLabel>Tenant</FormLabel>
              <Select
                placeholder="Select tenant..."
                value={formData.tenantId}
                onChange={e => handleInputChange('tenantId', e.target.value)}
              >
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.firstName} {tenant.lastName} - {tenant.unitNumber || 'No unit'}
                  </option>
                ))}
              </Select>
              {selectedTenant && (
                <FormHelperText>
                  Monthly Rent: ${selectedTenant.monthlyRent?.toFixed(2) || 'N/A'}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Amount</FormLabel>
              <NumberInput
                value={formData.amount}
                onChange={value => handleInputChange('amount', value)}
                min={0}
                step={0.01}
                precision={2}
              >
                <NumberInputField placeholder="0.00" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Payment Date</FormLabel>
              <Input
                type="date"
                value={formData.paymentDate}
                onChange={e => handleInputChange('paymentDate', e.target.value)}
              />
              {daysLate > 0 && (
                <FormHelperText color="orange.600">
                  {daysLate} days late
                </FormHelperText>
              )}
              {daysLate === 0 && (
                <FormHelperText color="green.600">
                  On time
                </FormHelperText>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Payment Method</FormLabel>
              <Select
                value={formData.method}
                onChange={e => handleInputChange('method', e.target.value)}
              >
                {PAYMENT_METHODS.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Reference/Check Number</FormLabel>
              <Input
                placeholder="e.g., Check #1234, Reference ID"
                value={formData.reference}
                onChange={e => handleInputChange('reference', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Input
                placeholder="Additional notes..."
                value={formData.notes}
                onChange={e => handleInputChange('notes', e.target.value)}
              />
            </FormControl>

            <Box width="100%" p={3} bg="blue.50" borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" color="blue.900">
                Approval Flow
              </Text>
              <Text fontSize="xs" color="blue.800" mt={1}>
                This payment will be sent to the owner for review and approval. Once approved, it will be posted to the tenant&apos;s account.
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Submitting..."
          >
            Log Payment
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LogPaymentModal;
